import { useTradeInfo } from "@/redux/selectors/tradeInfoSelector";
import { useAccountDynamicInfo, useAccountInfo } from "@/redux/selectors/accountSelector";
import { useTradeOrders } from "@/redux/selectors/ordersSelector";
import { useEffect } from "react";
import { getTradeProfit } from "@/utils/trade";
import { isBuyTrade } from "@/components/TradesTable/components/TableItem";
import { IDynamicAccountInfoResponse, quoteSocketManager } from "@/hooks/trade/tradeSocket";
import { setDynamicAccountInfo, setDynamicAccountInfoCalculated } from "@/redux/actions/account";
import { useDispatch } from "react-redux";

const useInterval = (callback, delay) => {
    useEffect(() => {
        const id = setInterval(callback, delay);
        return () => clearInterval(id);
    }, [callback, delay]);
};

export const useBalanceCalculations = (): any => {
    const { symbolsInfoById, symbols } = useTradeInfo();
    const { currency: accountCurrency } = useAccountInfo();
    const { balance = 0, credit = 0, margin = 0 } = useAccountDynamicInfo();
    const { openTrades } = useTradeOrders();
    const dispatch = useDispatch();

    useInterval(() => {
        let purePnL = 0;
        let swap = 0;
        let commission = 0;

        const cachedSymbolInfo = new Map<string, any>();
        const cachedQuotes = new Map<string, any>();

        for (const order of openTrades) {
            const cleanSymbol = order.symbol.replace("!", "");

            let symbolInfo = cachedSymbolInfo.get(order.symbol);
            if (!symbolInfo) {
                symbolInfo = symbolsInfoById[order.symbol] || symbolsInfoById[cleanSymbol];
                cachedSymbolInfo.set(order.symbol, symbolInfo);
            }

            let q = cachedQuotes.get(order.symbol);
            if (!q) {
                q = quoteSocketManager.getValue(order.symbol);
                if (!q?.ask) {
                    q = quoteSocketManager.getValue(cleanSymbol);
                }
                cachedQuotes.set(order.symbol, q);
            }

            if (!symbolInfo || !q) continue;

            const profit = getTradeProfit({
                openPrice: order.openPrice,
                ask: q.ask,
                bid: q.bid,
                tradeType: isBuyTrade(order) ? "Buy" : "Sell",
                lotSize: order.lots,
                accountCurrency,
                symbolInfo,
                symbols,
            });

            swap += order.swap;
            commission += order.commission;
            purePnL += profit;
        }

        const equity = balance + credit + purePnL + swap + commission;
        const freeMargin = equity - margin;
        const PnL = purePnL + swap + commission;

        dispatch(setDynamicAccountInfo({
            accountEquity: equity,
            accountFreeMargin: freeMargin,
            accountProfit: PnL,
        }));
    }, 500);
};

